/**
 * SetupWizardScreen — first-run admin provisioning.
 *
 * Shown when GET /api/v1/setup/status returns { first_run: true }. Single
 * full-screen form: firm name, admin name, admin email, password. Submits
 * to /api/v1/auth/local/setup. On success the response sets the session
 * cookie and we redirect to /. Subsequent visits skip the wizard.
 *
 * Citadel Light theme — matches SignInSplash, LocalSignInForm and
 * ChangePasswordScreen. Cream background, white card with crimson accent
 * rule above the form, navy serif title.
 *
 * Debug instrumentation: state registered, form events logged,
 * data-debug-id on every input. Per latest/ENGINEERING-PRACTICES.md.
 */

import { useEffect, useState } from 'react';
import { PRODUCT_NAME } from '@cipherops/data-source';
import { useDebugRegister, debugLog } from '../lib/debug';

interface Props {
  /** Where to navigate after success. Defaults to '/'. */
  onSuccessRedirect?: string;
}

export function SetupWizardScreen({ onSuccessRedirect }: Props) {
  const [firmName, setFirmName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordMatches = password.length > 0 && password === confirmPassword;
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const canSubmit =
    firmName.trim().length > 0 &&
    displayName.trim().length > 0 &&
    emailValid &&
    password.length >= 12 &&
    passwordMatches &&
    !submitting;

  useDebugRegister('SetupWizardScreen', {
    has_firm: firmName.trim().length > 0,
    has_display_name: displayName.trim().length > 0,
    email_valid: emailValid,
    password_len: password.length,
    passwords_match: passwordMatches,
    submitting,
    error,
  });

  useEffect(() => {
    document.title = `First-run setup · ${PRODUCT_NAME}`;
    debugLog.recordEvent({
      type: 'setup_wizard_mounted',
      description: 'SetupWizardScreen mounted (first-run state confirmed)',
    });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    debugLog.recordEvent({
      type: 'setup_submitted',
      description: 'first-run admin provisioning submitted',
      data: { firm_name: firmName.trim(), email: email.trim().toLowerCase() },
    });
    try {
      const startedAt = performance.now();
      const r = await fetch('/api/v1/auth/local/setup', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firm_name: firmName.trim(),
          email: email.trim().toLowerCase(),
          display_name: displayName.trim(),
          password,
        }),
      });
      const durationMs = performance.now() - startedAt;
      debugLog.recordApiCall({
        channel: '/api/v1/auth/local/setup',
        path: '/api/v1/auth/local/setup',
        payload: {
          firm_name: firmName.trim(),
          email: email.trim().toLowerCase(),
          display_name: displayName.trim(),
          password: '[REDACTED]',
        },
        ok: r.ok,
        status: r.status,
        durationMs,
      });

      if (!r.ok) {
        const j = await r.json().catch(() => null);
        const message = j?.message ?? `Setup failed (${r.status})`;
        debugLog.recordEvent({
          type: 'setup_failed',
          description: `${j?.code ?? `HTTP_${r.status}`}: ${message}`,
          data: { code: j?.code, status: r.status },
        });
        setError(message);
        setSubmitting(false);
        return;
      }
      const data = await r.json();
      debugLog.recordEvent({
        type: 'setup_succeeded',
        description: 'first-run firm + admin provisioned',
        data: { firm_id: data?.firm_id, user_id: data?.user_id },
      });
      window.location.href = data?.redirect ?? onSuccessRedirect ?? '/';
    } catch (err) {
      debugLog.recordEvent({
        type: 'setup_network_error',
        description: 'fetch failed before response',
        data: { error: err instanceof Error ? err.message : String(err) },
      });
      setError('Could not reach the server. Try again.');
      setSubmitting(false);
    }
  }

  return (
    <main style={pageStyle} data-debug-id="setup-wizard">
      <section style={cardStyle}>
        <div style={eyebrowStyle}>First-run setup</div>
        <h1 style={titleStyle}>
          {PRODUCT_NAME}<span style={{ color: 'var(--sev-high-label, #c44a1a)' }}>.</span>
        </h1>
        <p style={subtitleStyle}>
          Welcome — let's set up your firm administrator account. This
          wizard is only available once. After today, consultants are
          invited from Members → Invite.
        </p>

        <form onSubmit={handleSubmit} style={formStyle}>
          <h2 style={sectionHeadStyle}>Provision your firm</h2>

          <label style={labelStyle}>
            <span>Firm name</span>
            <input
              type="text"
              value={firmName}
              onChange={(e) => setFirmName(e.target.value)}
              placeholder="Citadel Security"
              disabled={submitting}
              autoFocus
              required
              data-debug-id="setup-firm-name"
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            <span>Your name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Jane Smith"
              disabled={submitting}
              required
              data-debug-id="setup-display-name"
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              autoComplete="email"
              disabled={submitting}
              required
              data-debug-id="setup-email"
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            <span>Password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
              disabled={submitting}
              required
              minLength={12}
              data-debug-id="setup-password"
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            <span>Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              required
              minLength={12}
              data-debug-id="setup-confirm-password"
              style={inputStyle}
            />
            {confirmPassword && !passwordMatches && (
              <small style={hintStyle}>Passwords don't match.</small>
            )}
          </label>

          {error && (
            <div style={errorStyle} data-debug-id="setup-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-debug-id="setup-submit"
            style={canSubmit ? submitButtonStyle : submitButtonDisabledStyle}
          >
            {submitting ? 'Provisioning…' : 'Create firm and sign in'}
          </button>

          <p style={legendStyle}>
            You'll be created as firm_admin. Other consultants are invited
            from the Members page after sign-in.
          </p>
        </form>

        <p style={footerStyle}>
          Press <kbd style={kbdStyle}>Ctrl+Shift+D</kbd> at any time to open the Debug Panel.
        </p>
      </section>
    </main>
  );
}

// ── Citadel Light cream theme (shared with the other auth screens) ────────

const pageStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '24px',
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  background: 'var(--bg, #f6f4f0)',
};

const cardStyle: React.CSSProperties = {
  maxWidth: 480,
  width: '100%',
  background: 'var(--surface, #ffffff)',
  border: '0.5px solid var(--border-light, #e0ddd8)',
  padding: '40px 36px',
};

const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--mono, "Courier New", monospace)',
  fontSize: 10,
  letterSpacing: 3,
  textTransform: 'uppercase',
  color: 'var(--text-muted, #7a7d8a)',
  marginBottom: 8,
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--serif, Georgia, serif)',
  fontSize: 28,
  fontWeight: 'normal',
  margin: '0 0 8px',
  textAlign: 'center',
};

const subtitleStyle: React.CSSProperties = {
  color: 'var(--text-muted, #7a7d8a)',
  fontSize: 13,
  lineHeight: 1.7,
  margin: '0 0 24px',
  textAlign: 'center',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  borderTop: '1.5px solid var(--accent, #8b1a2e)',
  paddingTop: 20,
};

const sectionHeadStyle: React.CSSProperties = {
  fontFamily: 'var(--serif, Georgia, serif)',
  fontSize: 18,
  fontWeight: 'normal',
  margin: '0 0 4px',
  textAlign: 'center',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 11,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'var(--text-muted, #7a7d8a)',
  fontFamily: 'var(--mono, "Courier New", monospace)',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  border: '0.5px solid var(--border, #d0ccc4)',
  borderRadius: 0,
  background: 'var(--surface, #ffffff)',
  color: 'var(--text, #1a1f2e)',
  textTransform: 'none',
  letterSpacing: 0,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--sev-high-label, #c44a1a)',
  fontFamily: 'var(--sans, system-ui, sans-serif)',
  textTransform: 'none',
  letterSpacing: 0,
  marginTop: 2,
};

const errorStyle: React.CSSProperties = {
  background: 'rgba(196, 74, 26, 0.08)',
  borderLeft: '3px solid var(--sev-high-border, #c44a1a)',
  color: 'var(--sev-high-label, #c44a1a)',
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.5,
};

const submitButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: 'var(--brand, #1a1f2e)',
  color: '#ffffff',
  fontSize: 14,
  letterSpacing: 0.3,
  border: 'none',
  borderRadius: 0,
  cursor: 'pointer',
  marginTop: 6,
};

const submitButtonDisabledStyle: React.CSSProperties = {
  ...submitButtonStyle,
  background: 'var(--text-muted, #7a7d8a)',
  cursor: 'not-allowed',
};

const legendStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted, #7a7d8a)',
  lineHeight: 1.6,
  textAlign: 'center',
  margin: '8px 0 0',
};

const footerStyle: React.CSSProperties = {
  marginTop: 24,
  fontSize: 11,
  color: 'var(--text-muted, #7a7d8a)',
  lineHeight: 1.6,
  paddingTop: 14,
  borderTop: '1px solid var(--border-light, #e0ddd8)',
  textAlign: 'center',
};

const kbdStyle: React.CSSProperties = {
  fontFamily: 'var(--mono, "Courier New", monospace)',
  fontSize: 10,
  background: 'var(--code-bg, #f0ede8)',
  border: '0.5px solid var(--border, #d0ccc4)',
  padding: '1px 5px',
  letterSpacing: 0.5,
};
